/**
 * Terms and Conditions Page
 *
 * Displays the legal terms and conditions for using ClosedLoopDiving services.
 */

import { Layout } from '@/components/Layout';

// Terms content - kept in sync with terms-and-conditions.md
const termsContent = `
# Terms and Conditions

**Last Updated: January 31, 2026**

Welcome to ClosedLoopDiving.com ("Website," "we," "us," or "our"). By accessing or using this Website, you acknowledge that you have read, understood, and agree to be bound by these Terms and Conditions. If you do not agree to these terms, please do not use this Website.

---

## 1. Educational Purpose and Scope

### 1.1 We Are Not a Training Agency

**CLOSEDLOOPDIVING IS NOT A SCUBA TRAINING AGENCY, CERTIFICATION BODY, OR DIVING INSTRUCTOR.** We do not:

- Issue diving certifications of any kind
- Conduct in-water training or skills assessments
- Evaluate diver competency or readiness
- Authorize or qualify anyone to perform any type of dive
- Operate as a training facility or dive school

### 1.2 Supplementary Study Tool Only

This Website provides **supplementary educational content** designed exclusively to reinforce concepts covered in formal, instructor-led technical diving courses. The materials on this Website are intended as a study aid and knowledge retention tool for students who are actively enrolled in or have completed certification courses with qualified diving instructors.

**This material is provided as an adjunct only to actual certification carried out under the direct supervision of a licensed scuba instructor affiliated with a recognized training agency.**

### 1.3 Not a Certification Course

**THIS WEBSITE DOES NOT PROVIDE DIVING CERTIFICATION OF ANY KIND.** The content presented here:

- Does not constitute a diving course, certification program, or qualification
- Does not replace, substitute for, or serve as an equivalent to formal instructor-led training
- Does not qualify any individual to conduct dives of any kind
- Does not grant any diving credentials, certifications, or endorsements
- Is not recognized by any certification agency as a training program

### 1.4 Completion Does Not Equal Certification

**COMPLETING ANY COURSE, MODULE, QUIZ, OR CONTENT ON THIS WEBSITE DOES NOT CERTIFY, QUALIFY, OR AUTHORIZE YOU TO PERFORM DECOMPRESSION DIVES, REBREATHER DIVES, TECHNICAL DIVES, OR ANY OTHER TYPE OF DIVING ACTIVITY.**

You are **not immediately able to complete decompression dives** or any technical dives after finishing training on this platform. Only completion of a full certification course with a qualified instructor, including all required in-water training, skills demonstrations, and assessments, can qualify you to perform such dives.

### 1.5 Formal Training Required

Technical diving, including but not limited to rebreather diving, decompression diving, mixed gas diving, and any form of overhead environment diving, requires comprehensive hands-on training from a qualified instructor affiliated with a recognized certification agency (such as PADI, TDI, IANTD, GUE, or equivalent). **No amount of self-study, including use of this Website, can substitute for direct instruction, supervised skill development, and in-water training.**

---

## 2. Assumption of Risk and Liability Disclaimer

### 2.1 Inherent Risks of Diving

Scuba diving and technical diving are inherently dangerous activities that can result in serious injury or death. These risks include, but are not limited to: drowning, decompression sickness, arterial gas embolism, oxygen toxicity, hypercapnia, hypothermia, barotrauma, equipment failure, and other diving-related injuries.

### 2.2 Disclaimer of Liability

**TO THE FULLEST EXTENT PERMITTED BY LAW, CLOSEDLOOPDIVING, ITS OWNERS, OPERATORS, EMPLOYEES, AFFILIATES, AND CONTENT CONTRIBUTORS SHALL NOT BE LIABLE FOR ANY INJURY, DEATH, LOSS, OR DAMAGE OF ANY KIND ARISING FROM:**

- The use or misuse of information presented on this Website
- Reliance on this Website's content in the absence of formal training
- Application of concepts learned from this Website without proper instructor guidance
- Any diving activity undertaken by users of this Website
- Errors, omissions, or inaccuracies in the content presented
- Outdated information that may not reflect current best practices or standards

### 2.3 No Professional Relationship

Use of this Website does not create an instructor-student relationship, professional relationship, or duty of care between ClosedLoopDiving and any user. We do not provide personalized instruction, evaluate individual competency, or assess fitness to dive.

---

## 3. User Responsibilities

### 3.1 Acknowledgment of Limitations

By using this Website, you acknowledge and agree that:

- You understand this content is supplementary and does not replace formal training
- You will not attempt any dive or diving technique based solely on information from this Website
- You will seek and complete appropriate certification courses from qualified instructors before engaging in any diving activities discussed herein
- You are solely responsible for your own safety and diving decisions
- You will verify all information with your instructor and current certification agency standards

### 3.2 Minimum Qualifications

The content on this Website is intended for individuals who:

- Are actively enrolled in a formal technical diving course, OR
- Have already completed relevant certifications and are using this material for review and continuing education
- Understand the prerequisite knowledge and experience required for technical diving

### 3.3 Not for Untrained Individuals

**If you have not received formal training in the topics covered on this Website, this content is not intended for you.** Technical diving concepts require proper context, hands-on practice, and instructor supervision to be safely understood and applied.

---

## 4. Content Accuracy and Currency

### 4.1 Informational Purposes Only

All content on this Website is provided for informational purposes only. While we strive for accuracy, diving standards, best practices, equipment specifications, and scientific understanding evolve over time.

### 4.2 No Warranty

We make no representations or warranties of any kind, express or implied, regarding the completeness, accuracy, reliability, suitability, or availability of any information on this Website. Any reliance you place on such information is strictly at your own risk.

### 4.3 Consult Your Instructor

Always consult with your diving instructor, certification agency materials, and equipment manufacturers for the most current and applicable guidance. In case of any discrepancy between this Website and official certification agency standards, **the certification agency standards prevail.**

---

## 5. Intellectual Property

All content on this Website, including text, graphics, images, videos, and other materials, is the property of ClosedLoopDiving or its content licensors and is protected by copyright and other intellectual property laws. You may not reproduce, distribute, or create derivative works without express written permission.

---

## 6. Third-Party Links and Resources

This Website may contain links to third-party websites or resources. We are not responsible for the content, accuracy, or practices of any third-party sites. Inclusion of any link does not imply endorsement.

---

## 7. Indemnification

You agree to indemnify, defend, and hold harmless ClosedLoopDiving, its owners, operators, employees, and affiliates from any claims, damages, losses, liabilities, costs, or expenses (including reasonable attorneys' fees) arising from your use of this Website, violation of these Terms, or any diving activities you undertake.

---

## 8. Governing Law

These Terms and Conditions shall be governed by and construed in accordance with the laws of Australia, without regard to its conflict of law provisions. Any disputes arising under these Terms shall be subject to the exclusive jurisdiction of the courts located in Australia.

---

## 9. Severability

If any provision of these Terms is found to be unenforceable or invalid, that provision shall be limited or eliminated to the minimum extent necessary, and the remaining provisions shall remain in full force and effect.

---

## 10. Changes to Terms

We reserve the right to modify these Terms and Conditions at any time. Changes will be effective immediately upon posting to this Website. Your continued use of the Website following any changes constitutes acceptance of the revised Terms.

---

## 11. Contact Information

If you have questions about these Terms and Conditions, please contact us at:

**ClosedLoopDiving**
info@closedloopdiving.com

---

## Acknowledgment

**BY USING THIS WEBSITE, YOU ACKNOWLEDGE THAT YOU HAVE READ THESE TERMS AND CONDITIONS, UNDERSTAND THEM, AND AGREE TO BE BOUND BY THEM. YOU FURTHER ACKNOWLEDGE THAT THIS WEBSITE IS A SUPPLEMENTARY STUDY TOOL ONLY AND DOES NOT REPLACE THE NEED FOR FORMAL, INSTRUCTOR-LED TRAINING FROM A QUALIFIED DIVING PROFESSIONAL.**

---

*Dive safe. Train properly. Never dive beyond your certification and experience level.*
`;

export function Terms() {
  // Simple markdown-to-HTML conversion for the terms content
  const renderMarkdown = (content: string) => {
    return content
      .split('\n')
      .map((line, index) => {
        // H1
        if (line.startsWith('# ')) {
          return (
            <h1
              key={index}
              style={{
                color: 'var(--cld-color-primary)',
                fontFamily: 'var(--cld-font-display)',
                fontSize: 'var(--cld-font-size-2xl)',
                fontWeight: 'var(--cld-font-weight-bold)',
                marginTop: 'var(--cld-spacing-xl)',
                marginBottom: 'var(--cld-spacing-md)',
              }}
            >
              {line.replace('# ', '')}
            </h1>
          );
        }
        // H2
        if (line.startsWith('## ')) {
          return (
            <h2
              key={index}
              style={{
                color: 'var(--cld-color-primary)',
                fontFamily: 'var(--cld-font-display)',
                fontSize: 'var(--cld-font-size-xl)',
                fontWeight: 'var(--cld-font-weight-semibold)',
                marginTop: 'var(--cld-spacing-lg)',
                marginBottom: 'var(--cld-spacing-sm)',
              }}
            >
              {line.replace('## ', '')}
            </h2>
          );
        }
        // H3
        if (line.startsWith('### ')) {
          return (
            <h3
              key={index}
              style={{
                color: 'var(--cld-color-primary)',
                fontFamily: 'var(--cld-font-display)',
                fontSize: 'var(--cld-font-size-lg)',
                fontWeight: 'var(--cld-font-weight-semibold)',
                marginTop: 'var(--cld-spacing-md)',
                marginBottom: 'var(--cld-spacing-xs)',
              }}
            >
              {line.replace('### ', '')}
            </h3>
          );
        }
        // Horizontal rule
        if (line.trim() === '---') {
          return (
            <hr
              key={index}
              style={{
                border: 'none',
                borderTop: '1px solid var(--cld-color-gray)',
                margin: 'var(--cld-spacing-lg) 0',
              }}
            />
          );
        }
        // List item
        if (line.startsWith('- ')) {
          return (
            <li
              key={index}
              style={{
                color: 'var(--cld-color-gray)',
                marginLeft: 'var(--cld-spacing-md)',
                marginBottom: 'var(--cld-spacing-xs)',
              }}
            >
              {line.replace('- ', '')}
            </li>
          );
        }
        // Bold text handling and regular paragraph
        if (line.trim()) {
          // Replace **text** with bold spans
          const parts = line.split(/(\*\*[^*]+\*\*)/g);
          return (
            <p
              key={index}
              style={{
                color: 'var(--cld-color-gray)',
                lineHeight: 1.7,
                marginBottom: 'var(--cld-spacing-sm)',
              }}
            >
              {parts.map((part, i) => {
                if (part.startsWith('**') && part.endsWith('**')) {
                  return (
                    <strong key={i} style={{ color: 'var(--cld-color-primary)' }}>
                      {part.slice(2, -2)}
                    </strong>
                  );
                }
                return part;
              })}
            </p>
          );
        }
        return null;
      })
      .filter(Boolean);
  };

  return (
    <Layout>
      <div
        style={{
          maxWidth: '800px',
          margin: '0 auto',
          padding: 'var(--cld-spacing-xl)',
        }}
      >
        <div
          style={{
            backgroundColor: 'var(--cld-color-white)',
            borderRadius: 'var(--cld-radius-lg)',
            padding: 'var(--cld-spacing-xl)',
            boxShadow: 'var(--cld-shadow-md)',
          }}
        >
          {renderMarkdown(termsContent)}
        </div>
      </div>
    </Layout>
  );
}

export default Terms;
